import { V2_TIER_BY_TRIAL_PLAN, isV2Plan, isV2TrialPlan, v2TierFor } from '@highlight/common/types'
import type {
  BillingPlan,
  BillingPlanV2Tier,
  BillingPlanV2TrialTier,
  CardOnFile,
  CreditBlock,
  SubscriptionInfo,
  UsageItem,
} from '@highlight/common/types'

import type { MeetingGateReason } from '@shared/meeting-gating-events'

export { v2TierFor } from '@highlight/common/types'

/**
 * Cancelled with the cancellation date passed: the transient window where the
 * hydrated plan still names its tier before flipping to free. The one state
 * where selling anything but a renewal is wrong — the plan-ended strip shows
 * and the top-up paywalls stand down.
 */
export const isPlanEnded = (subscription: SubscriptionInfo): boolean =>
  subscription.cancelled === true && (subscription.daysUntilCancellation ?? 1) <= 0

/**
 * Calculate days remaining between two dates
 */
export const calculateDaysRemaining = (params: { startedAt: string; endingAt: string }): number | null => {
  const now = new Date()
  const endDate = new Date(params.endingAt)
  const diffTime = endDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

/**
 * Calculate days remaining from a single date (end date)
 */
export const calculateDaysRemainingFromDate = (endDate?: string | null): number | null => {
  if (!endDate) return null
  const now = new Date()
  const end = new Date(endDate)
  const diffTime = end.getTime() - now.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const PAID_PLANS = ['pro', 'lite', 'pro2', 'max'] as const

/** Paid (non-trial) plans on the current billing system, including the v2 tiers. */
export type PaidPlan = (typeof PAID_PLANS)[number]

/** Narrow a plan string to a paid plan; trials, free, and legacy plans give null. */
export const asPaidPlan = (plan?: string | null): PaidPlan | null =>
  plan != null && (PAID_PLANS as ReadonlyArray<string>).includes(plan) ? (plan as PaidPlan) : null

/**
 * What an upgrade CTA can sell. Only the v2 tiers are on sale. Legacy pro is
 * priced for its existing subscribers but can no longer be bought.
 */
export type UpgradeTarget = BillingPlanV2Tier

/**
 * The paid plan an upgrade CTA sells. The user's own plan wins: a v2 trial
 * converts to its tier, so a lite trial is never sold pro2. Null for paid v2
 * users, who manage their plan instead of buying another one.
 *
 * `previousPlan` is what the account held before it dropped to free, and it
 * wins over the pro2 default so a lapsed Lite subscriber is sold Lite back
 * rather than upsold. It only decides anything for a free plan: a legacy plan
 * (pro, eat, unlimited, the pro-monthly/yearly pair) is still on the plan it
 * would be "returning" to, and pro2 is the tier that replaces all of them.
 */
export const getUpgradeTarget = (plan: string | undefined, previousPlan?: string | undefined): UpgradeTarget | null => {
  if (isV2TrialPlan(plan)) return V2_TIER_BY_TRIAL_PLAN[plan]
  if (isV2Plan(plan)) return null
  if (plan === 'free') {
    const previousTier = v2TierFor(previousPlan)
    if (previousTier) return previousTier
  }
  return 'pro2'
}

export type UpgradeRoute = { kind: 'manage-subscription' } | { kind: 'purchase'; target: UpgradeTarget }

/**
 * Where a paywall's upgrade CTA sends the user: paid v2 users manage their
 * plan, everyone else buys getUpgradeTarget's plan. The v2 tiers grant their
 * trial from the onboarding trial step only, so no later CTA offers one.
 */
export const resolveUpgradeRoute = (params: {
  plan: string | undefined
  previousPlan?: string | undefined
}): UpgradeRoute => {
  const target = getUpgradeTarget(params.plan, params.previousPlan)
  return target === null ? { kind: 'manage-subscription' } : { kind: 'purchase', target }
}

export type CardStepUpCheckout = { mode: 'setup' } | { mode: 'subscription'; tier: BillingPlanV2TrialTier }

/**
 * The checkout the card step-up opens. A card on file makes a v2 trial convert to its tier when it
 * ends, so a trial adds the card through its own Keep checkout, which states that conversion and its
 * price. Everyone else gets the plain card setup.
 */
export const resolveCardStepUpCheckout = (plan: string | undefined): CardStepUpCheckout =>
  isV2TrialPlan(plan) ? { mode: 'subscription', tier: V2_TIER_BY_TRIAL_PLAN[plan] } : { mode: 'setup' }

/**
 * The v2 tier a top-up paywall sells to, or null: a paid tier or its trial,
 * which tops up the same way. An ended plan needs a renewal first, and
 * legacy plans never map to a tier.
 */
export const topUpTier = (params: { plan: string | undefined; planEnded: boolean }): BillingPlanV2Tier | null => {
  const tier = v2TierFor(params.plan)
  return tier === null || params.planEnded ? null : tier
}

/**
 * A plan from the pre-v2 pricing system, including the pre-Orb ones. The
 * backend exempts these from credit enforcement (`resolveCreditStatus` is
 * v2-only), so they never reach a limit state, and no v2 surface can bill them
 * because none of them map to a tier. Surfaces that sell credits or meeting hours
 * check this and show nothing rather than pitching a v2 upgrade from a button
 * that says Add Credits. Moving to a v2 tier stays available in Settings.
 *
 * These accounts are being migrated onto v2 tiers, after which this and its
 * call sites can go.
 */
export const isLegacyPricingPlan = (plan?: string | null): boolean =>
  plan === 'pro' ||
  plan === 'pro-trial' ||
  // The server collapses 'eat' to 'pro-trial' before it reaches any client, so
  // this arm should be unreachable. Listed anyway: an omission here is silent.
  plan === 'eat' ||
  plan === 'unlimited' ||
  plan === 'pro-monthly' ||
  plan === 'pro-yearly'

export type MeetingLimitRoute = { kind: 'hours-top-up'; tier: BillingPlanV2Tier } | { kind: 'paywall-modal' }

/**
 * What a refused meeting start shows. A v2 tier out of hours gets the toast
 * whose button leads to the meeting-hours top-up. A v2 tier refused for no
 * entitlement holds a plan the server couldn't see, because billing was down
 * or the plan lapsed and this client hasn't heard. That is not a limit, so it
 * gets a toast saying so and nothing to buy. Everyone else is brought to the
 * sidepanel, where home docks the paywall.
 */
export type MeetingRefusalSurface = 'hours-toast' | 'plan-unverified-toast' | 'paywall'

export const resolveMeetingRefusalSurface = (
  reason: MeetingGateReason,
  route: MeetingLimitRoute,
): MeetingRefusalSurface => {
  if (route.kind !== 'hours-top-up') {
    return 'paywall'
  }
  return reason === 'hours' ? 'hours-toast' : 'plan-unverified-toast'
}

/**
 * Where the meeting-hours gate sends a refused start: a v2 plan, trial
 * included, gets the meeting-hours top-up. Everyone else keeps the upgrade paywall.
 */
export const resolveMeetingLimitRoute = (params: {
  plan: string | undefined
  planEnded: boolean
}): MeetingLimitRoute => {
  const tier = topUpTier(params)
  return tier === null ? { kind: 'paywall-modal' } : { kind: 'hours-top-up', tier }
}

/**
 * Get plan type flags from plan string. The v2 tiers (lite/pro2/max) count as
 * isPro so all paid-plan UI states apply to them; labels come from getPlanDisplayName.
 */
export const getPlanType = (plan?: BillingPlan | 'pro-trial' | string | null) => {
  const isFree = plan === 'free' || !plan
  const isTrial = plan === 'pro-trial' || plan === 'lite-trial' || plan === 'pro2-trial'
  const isPro = asPaidPlan(plan) !== null
  const isLegacy = plan === 'unlimited' || plan === 'pro-monthly' || plan === 'pro-yearly'

  return {
    isFree,
    isTrial,
    isPro,
    isLegacy,
  }
}

/** Display name for a plan on the current billing system. */
export const getPlanDisplayName = (plan?: BillingPlan | string | null): string => {
  switch (plan) {
    case 'free':
    case undefined:
    case null:
      return 'Free Plan'
    case 'pro':
      return 'Highlight Pro'
    case 'pro-trial':
      return 'Highlight Pro Trial'
    case 'lite':
      return 'Highlight Lite'
    case 'pro2':
      return 'Highlight Pro'
    case 'max':
      return 'Highlight Max'
    case 'lite-trial':
      return 'Highlight Lite Trial'
    case 'pro2-trial':
      return 'Highlight Pro Trial'
    default:
      return 'Plan'
  }
}

/**
 * Get valid credit blocks (not expired and effective)
 */
export const getValidCreditBlocks = (creditBlocks: CreditBlock[], includePurchased: boolean = true): CreditBlock[] => {
  const now = new Date()
  return creditBlocks.filter(
    (block) =>
      ((!block.expiryDate && includePurchased && block.balance > 0) ||
        (block.expiryDate && new Date(block.expiryDate) > now)) &&
      block.effectiveDate &&
      new Date(block.effectiveDate) <= now,
  )
}

/**
 * The two kinds of live credit a plan holds: the allowance that expires at the
 * end of the period, and top-ups bought on top of it, which never do. Both the
 * settings usage meters and the consumption block split blocks this way.
 */
export const splitCreditBlocks = (
  creditBlocks: Array<CreditBlock>,
): { monthly: Array<CreditBlock>; added: Array<CreditBlock> } => {
  const now = new Date()
  // Spent top-ups stay in `added`: getValidCreditBlocks drops a purchased block
  // once its balance hits zero, which is right for "what can I still spend" and
  // wrong for a used-out-of-total meter, where dropping it shrinks the
  // denominator and hides the spend entirely.
  const effective = creditBlocks.filter((block) => block.effectiveDate && new Date(block.effectiveDate) <= now)
  return {
    monthly: effective.filter((block) => block.expiryDate && new Date(block.expiryDate) > now),
    added: effective.filter((block) => !block.expiryDate),
  }
}

/**
 * Helper to calculate total available credits from credit blocks
 */
export const calculateTotalCredits = (creditBlocks: CreditBlock[]): number => {
  return creditBlocks.reduce((total, block) => total + block.balance, 0)
}

/**
 * Helper to calculate total initial credits from credit blocks
 */
export const calculateTotalInitialCredits = (creditBlocks: CreditBlock[]): number => {
  return creditBlocks.reduce((total, block) => total + (block.initialBalance || 0), 0)
}

/**
 * Helper to calculate usage percentage from credit blocks
 */
export const calculateUsagePercentage = (creditBlocks: CreditBlock[]): number => {
  const totalInitial = calculateTotalInitialCredits(creditBlocks)
  const totalRemaining = calculateTotalCredits(creditBlocks)

  // No allocation and no balance is spent, not untouched.
  if (totalInitial === 0) return totalRemaining > 0 ? 0 : 100

  const used = totalInitial - totalRemaining
  return Math.floor((used / totalInitial) * 100)
}

/** "Oct 15, 2026", for headers and tables where the date is one detail among several. */
export const formatShortDate = (date: string, timeZone?: string): string =>
  new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone })

/** "October 15, 2026", for copy where the date itself is the message. Local time unless a zone is given. */
export const formatPlanEndDate = (date: string, timeZone?: string): string =>
  new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone })

export const formatRenewalDate = (date: string, format: 'short' | 'long' = 'short'): string =>
  `Renews on ${format === 'long' ? formatPlanEndDate(date) : formatShortDate(date)}`

export const formatCancellationEndDate = (date: string): string => `Cancelled — Ends on ${formatPlanEndDate(date)}`

const CARD_BRAND_NAMES: Record<string, string> = {
  amex: 'Amex',
  cartes_bancaires: 'Cartes Bancaires',
  diners: 'Diners Club',
  discover: 'Discover',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
}

/** "Visa •••• 4242". Brands without a display name read as "Card". */
export const formatCardOnFile = ({ brand, last4 }: CardOnFile): string =>
  `${CARD_BRAND_NAMES[brand] ?? 'Card'} •••• ${last4}`

/**
 * Helper to get the total usage for a specific metric
 */
export const getTotalUsageForMetric = (usageData: UsageItem[], metricName: string): number => {
  const metric = usageData.find((item) => item.billableMetric.name === metricName)
  if (!metric) return 0

  // For cumulative view, take the latest data point
  if (metric.viewMode === 'cumulative' && metric.usage.length > 0) {
    return metric.usage[metric.usage.length - 1].quantity
  }

  // For periodic view, sum all usage
  return metric.usage.reduce((total, dataPoint) => total + dataPoint.quantity, 0)
}
